/**
 * A tenant's route prefix, on its own, with no environment behind it.
 *
 * It was in `app-origin.ts` beside `appOrigin()`, which is where it reads best —
 * and that put `process.env` into the import graph of every consumer. One of
 * them is the browser SDK: `buildPdappIconMarkup` writes a tenant path into the
 * icon's `href`, so `packages/sdk` started typechecking a module full of
 * `process.env` against DOM-only libs and failed with seven `Cannot find name
 * 'process'` errors. `pnpm build` runs that typecheck; the node-typed packages
 * do not see it.
 *
 * So the pure half lives here and `app-origin.ts` re-exports it — one
 * implementation, reachable from a browser bundle without dragging Node in.
 */

/**
 * `/~` is not "a personal drive" — it is the READER's OWN personal drive (#853). A
 * path into anyone else's tenant, personal or organization, has to NAME it
 * (`/t/<id>`), where the SPA resolves and gates it as whoever opens it: the owner of
 * `p_x` opening `/t/p_x/…` is in their own drive (the server treats it as their
 * personal tenant), anyone else meets that tenant's own access rules.
 *
 * So there are two forms, and the caller must know which it is building:
 *
 *  - {@link namedTenantPathPrefix} — for anything with NO single known reader: an href
 *    written into a page, a redirect, an email several people receive. Always
 *    `/t/<id>`.
 *  - {@link readerTenantPathPrefix} — for a path built FOR a reader the caller knows
 *    (the signed-in person): `/~` only when the tenant is that reader's own personal
 *    tenant.
 *
 * An empty tenant id is `''` in both — the caller's own fallback applies.
 */

/** `/t/<id>` — the tenant named, for a link with no single known reader. */
export function namedTenantPathPrefix(tenantId: string | null | undefined): string {
  if (!tenantId) return ''
  return `/t/${tenantId}`
}

/**
 * `/~` when `tenantId` is the reader's OWN personal tenant, else the named form. A
 * reader not known yet (`readerPersonalTenantId` empty — auth still loading) gets the
 * named form, which still opens the right tenant: it fails toward naming, never toward
 * someone else's `/~`.
 */
export function readerTenantPathPrefix(
  tenantId: string | null | undefined,
  readerPersonalTenantId: string | null | undefined,
): string {
  if (!tenantId) return ''
  return readerPersonalTenantId && tenantId === readerPersonalTenantId ? '/~' : `/t/${tenantId}`
}
